import React from "react";
import type { ApiTheme } from "../types";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/utils/date";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import type { RowSelectionState } from "@tanstack/react-table";
import MobileListWrapper from "@/components/mobile-list-wrapper";
import { formatSuccessRate } from "@/utils/success-rate";

interface ListContentProps {
  data: ApiTheme[];
  isLoading: boolean;
  className?: string;
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  emptyListComponent: React.ReactElement;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (selection: RowSelectionState) => void;
}

const ListContent = ({
  data,
  isLoading,
  className,
  page,
  pageCount,
  onPageChange,
  emptyListComponent,
  rowSelection,
  onRowSelectionChange,
}: ListContentProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <MobileListWrapper
      data={data}
      isLoading={isLoading}
      className={className}
      page={page}
      pageCount={pageCount}
      onPageChange={onPageChange}
      emptyListComponent={emptyListComponent}
      rowSelection={rowSelection}
      onRowSelectionChange={onRowSelectionChange}
      onItemClick={(theme) => navigate(ROUTES.THEME_DETAILS(theme.id))}
      renderItem={(theme) => (
        <>
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{theme.name}</h3>
            <Badge
              variant={theme.status === "active" ? "success" : "destructive"}
            >
              {theme.status === "active"
                ? t("keys.active")
                : t("keys.desactivated")}
            </Badge>
          </div>
          <div className="flex items-center mt-2 justify-between">
            <div className="flex items-center gap-2">
              <span
                className="size-4 rounded-full border border-gray-200 shrink-0"
                style={{ backgroundColor: theme.color }}
              />
              <span className="text-sm text-gray-500">{theme.color}</span>
            </div>
            <p className="text-sm text-gray-500">
              {formatDate(theme.created_at)}
            </p>
          </div>
          <div className="flex items-center justify-between gap-2 mt-2">
            <span className="text-xs text-gray-500">
              {t("keys.success_rate")}
            </span>
            <span className="text-sm text-gray-500">
              {formatSuccessRate(theme.success_rate)}
            </span>
          </div>
        </>
      )}
    />
  );
};

export default ListContent;
